// ============================================================================
// ContextChatPanel.tsx — контекстное обсуждение объекта (этап 2 мессенджера):
//   - ОДИН чат на объект: ФУНКЦИЯ ЯДРА getOrCreateContextThread (chatBridge =
//     sdk.messenger.getOrCreateContextThread) — идемпотентно (без дублирования);
//   - desktop: выдвижная панель СПРАВА (fixed, поверх окна объекта);
//     mobile: полноэкранный экран; ЕДИНЫЙ компонент (адаптив);
//   - внутри — ChatThread (лента/ввод/вложения/ответы/реакции/пересылка);
//   - прочтение: ChatThread.markReadOnOpen + обнуление бейджа (contextChat).
// ============================================================================

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, MessageSquare } from 'lucide-react';
import { friendlyError } from '@/lib/errors';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ChatThread } from '@/components/chat/ChatThread';
import { useContextChatStore } from '@/lib/contextChat';
import { getOrCreateContextThread } from '@/lib/chatBridge';

interface ContextChatPanelProps {
  open: boolean;
  onClose: () => void;
  /** Тип объекта (context_type): 'catalog' | 'document' | 'order' | ... */
  objectType: string;
  /** Код объекта метаданных (context_code — для настроек обсуждения) */
  objectCode: string;
  objectId: string;
  /** Заголовок обсуждения (синоним объекта) */
  objectTitle: string;
  myId: string;
  isAdmin: boolean;
}

export function ContextChatPanel({ open, onClose, objectType, objectCode, objectId, objectTitle, myId, isAdmin }: ContextChatPanelProps) {
  const { isMobile } = useBreakpoint();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Получение/создание обсуждения — ФУНКЦИЯ ЯДРА (единый механизм для приложения
  // чата и контекстных панелей: chatBridge / sdk.messenger.getOrCreateContextThread)
  useEffect(() => {
    if (!open || !objectId) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    (async () => {
      try {
        const { conversationId } = await getOrCreateContextThread({ objectType, objectCode, objectId, title: objectTitle });
        if (cancelled) return;
        setConversationId(conversationId);
      } catch (e: any) {
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, objectId, objectType, objectCode, objectTitle]);

  // Прочтение обнуляет бейдж контекста (точечно, без полного refresh)
  const markRead = useContextChatStore((s) => s.markRead);
  const refresh = useContextChatStore((s) => s.refresh);
  useEffect(() => {
    if (open && conversationId) {
      markRead(objectType, objectId, conversationId);
      void refresh();
    }
  }, [open, conversationId, objectType, objectId, markRead, refresh]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[12000] bg-black/30"
          onClick={onClose}
        >
          <motion.div
            initial={{ x: isMobile ? '100%' : 40, opacity: isMobile ? 0 : 1 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: isMobile ? '100%' : 40, opacity: isMobile ? 0 : 1 }}
            transition={{ type: 'tween', duration: 0.2 }}
            className={`absolute flex flex-col bg-background shadow-xl border-l ${
              isMobile ? 'inset-0 border-0' : 'right-0 top-0 bottom-0 w-[380px]'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Шапка панели */}
            <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
              <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-xs font-medium truncate flex-1">Обсуждение: {objectTitle}</span>
              <button className="p-1 rounded hover:bg-muted/40" onClick={onClose} aria-label="Закрыть">
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* Тело: ошибка / загрузка / переписка */}
            {error ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                  {error}
                </div>
              </div>
            ) : busy || !conversationId ? (
              <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
                Загрузка обсуждения…
              </div>
            ) : (
              <div className="flex-1 min-h-0">
                <ChatThread
                  key={conversationId}
                  conversationId={conversationId}
                  title={objectTitle}
                  contextType={objectType}
                  contextCode={objectCode}
                  contextId={objectId}
                  myId={myId}
                  isAdmin={isAdmin}
                  onConversationLeft={onClose}
                />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
